import { JsonValue } from '@components/json-editor';
import { FieldType } from '@components/text-editors/addons/field-mapping';

import { request } from 'api/request';
import { Condition, ConditionType, VariableValue } from 'api/types';

type variables = {
  name: string;
  type: FieldType;
  label: string;
};

export type JourneyWebhookList = {
  shortId: string;
  description: string;
  isActive: boolean;
  isAutoStart: boolean;
  journeyShortId: string;
  isConfigured: boolean;
};

export type JourneyWebhook = JourneyWebhookList & {
  triggerUrl: string;
  payloadVariables: variables[];
  headerVariables: variables[];
  fileVariables: variables[];
  logsCount: number;
  mappings: { [key: string]: VariableValue };
  conditions?: {
    conditions: Condition[];
    conditionType: ConditionType;
  };
};

export type JourneyWebhookData = Omit<
  JourneyWebhook,
  'payloadVariables' | 'headerVariables' | 'fileVariables'
> & {
  payloadVariables?: string;
  headerVariables?: string;
  fileVariables?: string;
};

export const loadJourneyWebhooks = async (journeyId: string) => {
  const response = await request.get<JourneyWebhookList[]>(
    `v1/journey-webhooks/?journey_id=${journeyId}`
  );
  return response.data;
};

export const loadTemplateJourneyWebhooks = async (journeyId: string) => {
  const response = await request.get<JourneyWebhookList[]>(
    `v1/templates/journey/${journeyId}/journey-webhooks/`
  );
  return response.data;
};

export const loadJourneyWebhook = async (webhookId: string) => {
  const response = await request.get<JourneyWebhook>(`v1/journey-webhooks/${webhookId}`);
  return response.data;
};

export const loadTemplateJourneyWebhook = async (webhookId: string) => {
  const response = await request.get<JourneyWebhook>(`v1/templates/journey-webhooks/${webhookId}/`);
  return response.data;
};

export const updateJourneyWebhook = async (
  webhookId: string,
  data: Partial<JourneyWebhookData>
) => {
  const response = await request.patch<JourneyWebhookData>(`v1/journey-webhooks/${webhookId}/`, {
    ...data,
    payloadVariables: data.payloadVariables ? JSON.parse(data.payloadVariables) : undefined,
    headerVariables: data.headerVariables ? JSON.parse(data.headerVariables) : undefined,
    fileVariables: data.fileVariables ? JSON.parse(data.fileVariables) : undefined,
  });
  return response.data;
};

export const deleteJourneyWebhook = async (webhookId: string) => {
  const response = await request.delete<void>(`v1/journey-webhooks/${webhookId}/`);
  return response.data;
};

export const createJourneyWebhook = async (journeyShortId: string) => {
  const response = await request.post<JourneyWebhook>('v1/journey-webhooks/', {
    journeyShortId,
  });
  return response.data;
};

export type JourneyWebhookLogs = {
  shortId: string;
  triggeredOnDate: Date;
  triggeredOnTime: Date;
};

export const loadJourneyWebhookLogs = async (webhookId: string) => {
  const response = await request.get<JourneyWebhookLogs[]>(`v1/journey-webhooks/${webhookId}/logs`);
  return response.data;
};

export type JourneyWebhookLog = Omit<JourneyWebhookLogs, 'triggeredOnTime'> & {
  payload: JsonValue;
  errorMessage: string;
};

export const loadJourneyWebhookLog = async (webhookId: string, webhookLogId: string) => {
  const response = await request.get<JourneyWebhookLog>(
    `v1/journey-webhooks/${webhookId}/logs/${webhookLogId}`
  );
  return response.data;
};
